import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import ProductEditor from '../components/ProductEditor';
import { Badge } from '@/components/ui/badge';
import { Layers, Ban, Sparkles } from 'lucide-react';

function Produkte() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showAdvancedEditor, setShowAdvancedEditor] = useState(false);
  const [advancedEditProduct, setAdvancedEditProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category_id: '',
    price: '',
    tax_rate: 19,
    sku: '',
    description: '',
    product_type: 'food',
    preparation_type: 'kitchen',
    track_inventory: false,
    stock_quantity: 0,
    low_stock_threshold: 5,
    is_active: true
  });

  const loadData = async () => {
    try {
      setLoading(true);
      const [productsData, categoriesData] = await Promise.all([
        api.get('/api/products/'),
        api.get('/api/products/categories')
      ]);
      setProducts(productsData);
      setCategories(categoriesData.filter(c => c.is_active));
    } catch (err) {
      setMessage({ type: 'error', text: 'Fehler beim Laden: ' + err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleCreate = async () => {
    // Validation
    if (!formData.name.trim()) {
      setMessage({ type: 'error', text: 'Produktname ist erforderlich' });
      return;
    }
    if (formData.name.trim().length < 2) {
      setMessage({ type: 'error', text: 'Produktname muss mindestens 2 Zeichen lang sein' });
      return;
    }
    if (!formData.category_id) {
      setMessage({ type: 'error', text: 'Kategorie ist erforderlich' });
      return;
    }
    if (!formData.price || parseFloat(formData.price) < 0) {
      setMessage({ type: 'error', text: 'Preis muss größer oder gleich 0 sein' });
      return;
    }
    if (parseFloat(formData.price) > 9999) {
      setMessage({ type: 'error', text: 'Preis darf nicht größer als 9999 € sein' });
      return;
    }

    try {
      // Format price to 2 decimals
      const dataToSend = {
        ...formData,
        price: parseFloat(formData.price).toFixed(2)
      };
      
      await api.post('/api/products', dataToSend);
      setMessage({ type: 'success', text: '✓ Produkt erstellt' });
      setShowCreateModal(false);
      resetForm();
      loadData();
    } catch (err) {
      setMessage({ type: 'error', text: 'Fehler: ' + err.message });
    }
  };

  const handleUpdate = async (productId, updates) => {
    try {
      await api.patch(`/api/products/${productId}`, updates);
      setMessage({ type: 'success', text: '✓ Gespeichert' });
      loadData();
      setTimeout(() => setMessage(null), 2000);
    } catch (err) {
      setMessage({ type: 'error', text: 'Fehler: ' + err.message });
    }
  };

  const handleToggleActive = async (product) => {
    await handleUpdate(product.product_id, { is_active: !product.is_active });
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Produkt wirklich deaktivieren?')) return;

    try {
      await api.delete(`/api/products/${productId}`);
      setMessage({ type: 'success', text: '✓ Produkt deaktiviert' });
      loadData();
    } catch (err) {
      setMessage({ type: 'error', text: 'Fehler: ' + err.message });
    }
  };

  const handleInlineEdit = async (productId, field, value) => {
    await handleUpdate(productId, { [field]: value });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category_id: '',
      price: '',
      tax_rate: 19,
      sku: '',
      description: '',
      product_type: 'food',
      preparation_type: 'kitchen',
      track_inventory: false,
      stock_quantity: 0,
      min_stock: 5,
      unit: 'Stück',
      is_active: true
    });
  };

  const filteredProducts = products.filter(p => {
    const matchesCategory = !selectedCategory || p.category_id === selectedCategory;
    const matchesSearch = !searchTerm || 
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.sku && p.sku.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
        <div style={{ color: 'var(--text-secondary)' }}>Lade Produkte...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div className="flex justify-between items-start mb-6 flex-wrap gap-3">
          <div className="flex-1 min-w-[200px]">
            <h1 className="page-title text-xl lg:text-2xl">Produkte</h1>
            <p className="page-subtitle text-sm lg:text-base">Artikel verwalten und bearbeiten</p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
            className="btn-primary text-sm lg:text-base whitespace-nowrap"
          >
            + Neues Produkt
          </button>
        </div>

        {/* Filters */}
        <div className="grid-2 mb-6">
          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: 'var(--space-2)', color: 'var(--text-primary)' }}>
              Kategorie filtern
            </label>
            <select
              value={selectedCategory}
              onChange={e => setSelectedCategory(e.target.value)}
              className="dark-select"
            >
              <option value="">Alle Kategorien</option>
              {categories.map(cat => (
                <option key={cat.category_id} value={cat.category_id}>
                  {cat.icon} {cat.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: 'var(--space-2)', color: 'var(--text-primary)' }}>
              Suche
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Name oder SKU..."
              className="dark-input"
            />
          </div>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={message.type === 'success' ? 'alert alert-success' : 'alert alert-error'}>
          {message.text}
        </div>
      )}

      {/* Products List */}
      {filteredProducts.length === 0 ? (
        <div className="dark-card" style={{ textAlign: 'center', padding: 'var(--space-8)', color: 'var(--text-secondary)' }}>
          <div style={{ fontSize: '3rem', marginBottom: 'var(--space-4)' }}>📦</div>
          <div style={{ fontSize: '1.125rem', marginBottom: 'var(--space-2)', color: 'var(--text-primary)', fontWeight: 600 }}>
            {products.length === 0 ? 'Noch keine Produkte' : 'Keine Produkte gefunden'}
          </div>
          <div style={{ fontSize: '0.875rem', marginBottom: 'var(--space-4)' }}>
            {products.length === 0 ? 'Legen Sie Ihr erstes Produkt an, um loszulegen' : 'Versuchen Sie andere Suchbegriffe oder Filter'}
          </div>
          {products.length === 0 ? (
            <div style={{ marginTop: 'var(--space-6)' }}>
              {categories.length === 0 ? (
                <div style={{ marginBottom: 'var(--space-4)', padding: 'var(--space-4)', background: 'var(--bg-tertiary)', borderRadius: '8px', maxWidth: '500px', margin: '0 auto' }}>
                  <div style={{ fontSize: '0.875rem', marginBottom: 'var(--space-3)', color: 'var(--text-secondary)' }}>
                    💡 Tipp: Erstellen Sie zuerst eine Kategorie
                  </div>
                  <a href="/kategorien" className="btn-secondary" style={{ display: 'inline-block' }}>
                    Zu Kategorien
                  </a>
                </div>
              ) : (
                <button
                  onClick={() => {
                    resetForm();
                    setShowCreateModal(true);
                  }}
                  className="btn-primary"
                  style={{ padding: 'var(--space-3) var(--space-6)' }}
                >
                  + Erstes Produkt erstellen
                </button>
              )}
            </div>
          ) : (
            <button
              onClick={() => {
                setSelectedCategory('');
                setSearchQuery('');
              }}
              className="btn-secondary"
              style={{ marginTop: 'var(--space-4)' }}
            >
              Filter zurücksetzen
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="dark-card hidden lg:block">
            <div className="dark-table-container">
              <table className="dark-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Kategorie</th>
                    <th style={{ width: '120px' }}>Preis</th>
                    <th style={{ width: '100px' }}>MwSt</th>
                    <th style={{ width: '100px' }}>Status</th>
                    <th style={{ width: '200px', textAlign: 'right' }}>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map(product => (
                    <tr key={product.product_id}>
                      <td>
                        <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                          {product.name}
                        </div>
                        {product.sku && (
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginTop: 'var(--space-1)' }}>
                            SKU: {product.sku}
                          </div>
                        )}
                        {/* Feature Badges */}
                        <div className="flex flex-wrap gap-1 mt-1">
                          {product.has_variants && product.variants?.length > 0 && (
                            <Badge variant="outline" className="text-xs flex items-center gap-1">
                              <Layers className="h-3 w-3" />
                              {product.variants.length} Größen
                            </Badge>
                          )}
                          {product.has_removable_ingredients && product.ingredients?.filter(i => i.is_removable)?.length > 0 && (
                            <Badge variant="destructive" className="text-xs flex items-center gap-1">
                              <Ban className="h-3 w-3" />
                              {product.ingredients.filter(i => i.is_removable).length} OHNE
                            </Badge>
                          )}
                          {product.has_extras && product.extra_groups?.length > 0 && (
                            <Badge variant="secondary" className="text-xs flex items-center gap-1 bg-emerald-100 text-emerald-800">
                              <Sparkles className="h-3 w-3" />
                              {product.extra_groups.reduce((sum, g) => sum + g.extras?.length, 0)} Extras
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td>
                        <span style={{ color: 'var(--text-secondary)' }}>
                          {product.category_name}
                        </span>
                      </td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          value={product.price}
                          onChange={e => handleInlineEdit(product.product_id, 'price', parseFloat(e.target.value))}
                          className="dark-input"
                          style={{ width: '100px', padding: 'var(--space-1) var(--space-2)', fontSize: '0.875rem' }}
                        />
                      </td>
                      <td>
                        <span style={{ color: 'var(--text-secondary)' }}>
                          {product.tax_rate}%
                        </span>
                      </td>
                      <td>
                        {product.is_active ? (
                          <span className="badge badge-success">Aktiv</span>
                        ) : (
                          <span className="badge badge-neutral">Inaktiv</span>
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
                          <button
                            onClick={() => handleToggleActive(product)}
                            className="btn-secondary"
                            style={{ padding: 'var(--space-2) var(--space-3)', fontSize: '0.875rem' }}
                          >
                            {product.is_active ? 'Aus' : 'An'}
                          </button>
                          <button
                            onClick={() => {
                              setAdvancedEditProduct(product);
                              setShowAdvancedEditor(true);
                            }}
                            className="btn-primary"
                            style={{ padding: 'var(--space-2) var(--space-3)', fontSize: '0.875rem' }}
                            title="Varianten, OHNE-Optionen und Extras bearbeiten"
                          >
                            ⚙️ Erweitert
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden space-y-3">
            {filteredProducts.map(product => (
              <div key={product.product_id} className="dark-card p-4">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-base text-[var(--text-primary)] mb-1">
                      {product.name}
                    </h3>
                    {product.sku && (
                      <p className="text-xs text-[var(--text-tertiary)]">SKU: {product.sku}</p>
                    )}
                    <p className="text-sm text-[var(--text-secondary)] mt-1">{product.category_name}</p>
                  </div>
                  {product.is_active ? (
                    <span className="badge badge-success text-xs">Aktiv</span>
                  ) : (
                    <span className="badge badge-neutral text-xs">Inaktiv</span>
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-3 mb-3 py-3 border-y border-[var(--border-primary)]">
                  <div>
                    <p className="text-xs text-[var(--text-secondary)] mb-1">Preis</p>
                    <p className="text-lg font-bold text-[var(--accent-primary)]">{product.price.toFixed(2)} €</p>
                  </div>
                  <div>
                    <p className="text-xs text-[var(--text-secondary)] mb-1">MwSt</p>
                    <p className="text-lg font-semibold">{product.tax_rate}%</p>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <button
                    onClick={() => handleToggleActive(product)}
                    className="btn-secondary flex-1 text-sm py-3"
                  >
                    {product.is_active ? '🔴 Deaktivieren' : '🟢 Aktivieren'}
                  </button>
                  <button
                    onClick={() => setEditingProduct(product)}
                    className="btn-primary flex-1 text-sm py-3"
                  >
                    ✏️ Bearbeiten
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || editingProduct) && (
        <div className="modal-overlay" onClick={() => {
          setShowCreateModal(false);
          setEditingProduct(null);
        }}>
          <div className="modal-container" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: 'var(--space-6)', color: 'var(--text-primary)' }}>
              {editingProduct ? 'Produkt bearbeiten' : 'Neues Produkt'}
            </h2>
            
            <div className="grid-2 mb-4">
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: 'var(--space-2)', color: 'var(--text-primary)' }}>
                  Name *
                </label>
                <input
                  type="text"
                  value={editingProduct ? editingProduct.name : formData.name}
                  onChange={e => editingProduct 
                    ? setEditingProduct({ ...editingProduct, name: e.target.value })
                    : setFormData({ ...formData, name: e.target.value })
                  }
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      editingProduct ? handleUpdate(editingProduct.product_id, editingProduct) : handleCreate();
                    }
                    if (e.key === 'Escape') {
                      setShowCreateModal(false);
                      setEditingProduct(null);
                    }
                  }}
                  className="dark-input"
                  autoFocus
                  placeholder="z.B. Margherita"
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: 'var(--space-2)', color: 'var(--text-primary)' }}>
                  Kategorie *
                </label>
                <select
                  value={editingProduct ? editingProduct.category_id : formData.category_id}
                  onChange={e => editingProduct
                    ? setEditingProduct({ ...editingProduct, category_id: e.target.value })
                    : setFormData({ ...formData, category_id: e.target.value })
                  }
                  className="dark-select"
                >
                  <option value="">Wählen...</option>
                  {categories.map(cat => (
                    <option key={cat.category_id} value={cat.category_id}>
                      {cat.icon} {cat.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid-2 mb-4">
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: 'var(--space-2)', color: 'var(--text-primary)' }}>
                  Preis (€) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={editingProduct ? editingProduct.price : formData.price}
                  onChange={e => editingProduct
                    ? setEditingProduct({ ...editingProduct, price: e.target.value })
                    : setFormData({ ...formData, price: e.target.value })
                  }
                  className="dark-input"
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: 'var(--space-2)', color: 'var(--text-primary)' }}>
                  MwSt-Satz (%) *
                </label>
                <select
                  value={editingProduct ? editingProduct.tax_rate : formData.tax_rate}
                  onChange={e => editingProduct
                    ? setEditingProduct({ ...editingProduct, tax_rate: parseInt(e.target.value) })
                    : setFormData({ ...formData, tax_rate: parseInt(e.target.value) })
                  }
                  className="dark-select"
                >
                  <option value="19">19%</option>
                  <option value="7">7%</option>
                  <option value="0">0%</option>
                </select>
              </div>
            </div>

            <div style={{ marginBottom: 'var(--space-4)' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: 'var(--space-2)', color: 'var(--text-primary)' }}>
                SKU (optional)
              </label>
              <input
                type="text"
                value={editingProduct ? (editingProduct.sku || '') : formData.sku}
                onChange={e => editingProduct
                  ? setEditingProduct({ ...editingProduct, sku: e.target.value })
                  : setFormData({ ...formData, sku: e.target.value })
                }
                className="dark-input"
                placeholder="PROD-001"
              />
            </div>

            {/* Preparation Type Section */}
            <div style={{ marginBottom: 'var(--space-4)', padding: 'var(--space-4)', background: 'var(--bg-tertiary)', borderRadius: '8px', border: '1px solid var(--border-primary)' }}>
              <h3 style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: 'var(--space-2)', color: 'var(--text-primary)' }}>
                🧾 Ausgabe / Verarbeitung *
              </h3>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 'var(--space-3)' }}>
                Bestimmt, wo der Artikel verarbeitet wird und wie er auf dem Bon erscheint
              </p>
              
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: 'var(--space-2)', color: 'var(--text-primary)' }}>
                  Verarbeitung *
                </label>
                <select
                  value={editingProduct ? (editingProduct.preparation_type || 'kitchen') : formData.preparation_type}
                  onChange={e => editingProduct
                    ? setEditingProduct({ ...editingProduct, preparation_type: e.target.value })
                    : setFormData({ ...formData, preparation_type: e.target.value })
                  }
                  className="dark-select"
                >
                  <option value="kitchen">🍳 Küche – wird von der Küche zubereitet</option>
                  <option value="driver">🚗 Theke / Fahrer – wird vom Fahrer selbst eingepackt</option>
                  <option value="info">ℹ️ Info / Kein Druck – erscheint nicht auf Küchenbon</option>
                </select>
                
                {(editingProduct ? editingProduct.preparation_type : formData.preparation_type) === 'driver' && (
                  <div style={{ marginTop: 'var(--space-3)', padding: 'var(--space-3)', background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: 'var(--radius-md)' }}>
                    <p style={{ fontSize: '0.75rem', color: 'rgb(59, 130, 246)', lineHeight: 1.5 }}>
                      💡 <strong>Hinweis:</strong> Artikel wird auf dem Hauptbon mit schwarzem Hintergrund (invertiert) gedruckt, damit der Fahrer ihn sofort erkennt.
                    </p>
                  </div>
                )}
                
                {(editingProduct ? editingProduct.preparation_type : formData.preparation_type) === 'kitchen' && (
                  <div style={{ marginTop: 'var(--space-3)', padding: 'var(--space-3)', background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)', borderRadius: 'var(--radius-md)' }}>
                    <p style={{ fontSize: '0.75rem', color: 'rgb(34, 197, 94)', lineHeight: 1.5 }}>
                      ✓ Artikel erscheint auf dem Küchenbon und wird normal auf dem Hauptbon gedruckt.
                    </p>
                  </div>
                )}
                
                {(editingProduct ? editingProduct.preparation_type : formData.preparation_type) === 'info' && (
                  <div style={{ marginTop: 'var(--space-3)', padding: 'var(--space-3)', background: 'rgba(234, 179, 8, 0.1)', border: '1px solid rgba(234, 179, 8, 0.3)', borderRadius: 'var(--radius-md)' }}>
                    <p style={{ fontSize: '0.75rem', color: 'rgb(202, 138, 4)', lineHeight: 1.5 }}>
                      ⚠️ Artikel erscheint NICHT auf dem Küchenbon. Nur auf dem Hauptbon sichtbar.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Inventory Section */}
            <div style={{ marginBottom: 'var(--space-4)', padding: 'var(--space-4)', background: 'var(--bg-tertiary)', borderRadius: '8px', border: '1px solid var(--border-primary)' }}>
              <h3 style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: 'var(--space-3)', color: 'var(--text-primary)' }}>
                📦 Bestandsverwaltung (Optional)
              </h3>
              
              <div style={{ marginBottom: 'var(--space-3)' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={editingProduct ? editingProduct.track_inventory : formData.track_inventory}
                    onChange={e => editingProduct
                      ? setEditingProduct({ ...editingProduct, track_inventory: e.target.checked })
                      : setFormData({ ...formData, track_inventory: e.target.checked })
                    }
                    style={{ width: '16px', height: '16px' }}
                  />
                  <span style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>
                    Bestand tracken
                  </span>
                </label>
              </div>

              {(editingProduct ? editingProduct.track_inventory : formData.track_inventory) && (
                <div className="grid-2 mb-3" style={{ marginTop: 'var(--space-3)' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: 'var(--space-2)', color: 'var(--text-primary)' }}>
                      Aktueller Bestand
                    </label>
                    <input
                      type="number"
                      value={editingProduct ? editingProduct.stock_quantity : formData.stock_quantity}
                      onChange={e => editingProduct
                        ? setEditingProduct({ ...editingProduct, stock_quantity: parseInt(e.target.value) || 0 })
                        : setFormData({ ...formData, stock_quantity: parseInt(e.target.value) || 0 })
                      }
                      className="dark-input"
                      min="0"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: 'var(--space-2)', color: 'var(--text-primary)' }}>
                      Mindestmenge
                    </label>
                    <input
                      type="number"
                      value={editingProduct ? editingProduct.min_stock : formData.min_stock}
                      onChange={e => editingProduct
                        ? setEditingProduct({ ...editingProduct, min_stock: parseInt(e.target.value) || 0 })
                        : setFormData({ ...formData, min_stock: parseInt(e.target.value) || 0 })
                      }
                      className="dark-input"
                      min="0"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: 'var(--space-2)', color: 'var(--text-primary)' }}>
                      Einheit
                    </label>
                    <select
                      value={editingProduct ? editingProduct.unit : formData.unit}
                      onChange={e => editingProduct
                        ? setEditingProduct({ ...editingProduct, unit: e.target.value })
                        : setFormData({ ...formData, unit: e.target.value })
                      }
                      className="dark-select"
                    >
                      <option value="Stück">Stück</option>
                      <option value="kg">kg</option>
                      <option value="Liter">Liter</option>
                      <option value="Packung">Packung</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            <div style={{ marginBottom: 'var(--space-6)' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={editingProduct ? editingProduct.is_active : formData.is_active}
                  onChange={e => editingProduct
                    ? setEditingProduct({ ...editingProduct, is_active: e.target.checked })
                    : setFormData({ ...formData, is_active: e.target.checked })
                  }
                  style={{ width: '16px', height: '16px' }}
                />
                <span style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>
                  Produkt ist aktiv
                </span>
              </label>
            </div>

            <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingProduct(null);
                }}
                className="btn-secondary"
                style={{ flex: 1 }}
              >
                Abbrechen
              </button>
              <button
                onClick={async () => {
                  if (editingProduct) {
                    await handleUpdate(editingProduct.product_id, {
                      name: editingProduct.name,
                      category_id: editingProduct.category_id,
                      price: parseFloat(editingProduct.price),
                      tax_rate: parseInt(editingProduct.tax_rate),
                      sku: editingProduct.sku,
                      is_active: editingProduct.is_active
                    });
                    setEditingProduct(null);
                  } else {
                    await handleCreate();
                  }
                }}
                className="btn-primary"
                style={{ flex: 1 }}
              >
                {editingProduct ? 'Speichern' : 'Erstellen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Advanced Product Editor (Varianten, OHNE, Extras) */}
      <ProductEditor
        product={advancedEditProduct}
        isOpen={showAdvancedEditor}
        onClose={() => {
          setShowAdvancedEditor(false);
          setAdvancedEditProduct(null);
        }}
        onSave={() => {
          loadData();
          setShowAdvancedEditor(false);
          setAdvancedEditProduct(null);
        }}
        categories={categories}
      />
    </div>
  );
}

export default Produkte;
